import { Component, Inject, OnInit } from '@angular/core';
import { Bim360Service } from '../../../bim360/bim360.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { ToastrService } from 'ngx-toastr';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Bim360Object } from '../../bim-viewer/bim-viewer.component';
import { ObservationDetails } from '../../../../models/observations.model';
import { Project360Service } from '../../project360.service';
import { ObservationsService } from '../../../../services/Observations/observations.service';

@Component({
  selector: 'app-create-acc-issues-dialog',
  templateUrl: './create-acc-issues-dialog.component.html',
  styleUrls: ['./create-acc-issues-dialog.component.scss'],
})
export class CreateACCIssuesDialogComponent implements OnInit {
  newIssueForm: UntypedFormGroup;
  isLoading: boolean = false;
  bimProjectList: Bim360Object[];
  issue_types: { id: string; title: string }[];
  selectedProject: boolean = false;

  constructor(
    public bimService: Bim360Service,
    public dialogRef: MatDialogRef<CreateACCIssuesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { observationDetails: ObservationDetails },
    public transloco: TranslocoService,
    private _toaster: ToastrService,
    private fb: UntypedFormBuilder,
    public _project: Project360Service,
    private observationsService: ObservationsService,
  ) {
    this.newIssueForm = this.fb.group({
      project_id: [
        this._project.projectInfo.autodesk_account.autodesk_project_id,
        Validators.required,
      ],
      issueSubtypeId: [null, Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    this.bimService.bimProjectList$.subscribe(async (projects) => {
      this.bimProjectList = projects;

      if (
        !this._project.projectInfo.autodesk_account.autodesk_project_id &&
        this.bimProjectList.length
      ) {
        this.newIssueForm.controls.project_id.setValue(this.bimProjectList[0].id);
        await this.getBimIssuesTypes(this.bimProjectList[0].id);
      }
    });

    if (this._project.projectInfo.autodesk_account.autodesk_project_id) {
      await this.getBimIssuesTypes(this._project.projectInfo.autodesk_account.autodesk_project_id);
    }
  }

  createIssue() {
    if (this.newIssueForm.valid) {
      this.isLoading = true;
      let dueDate =
        !this.data.observationDetails?.due_date || this.data.observationDetails?.due_date !== 'N/A'
          ? new Date(this.data.observationDetails?.due_date).toISOString().split('T')[0] //send date only without time
          : null;
      let body = {
        title: this.data.observationDetails?.title,
        description: this.data.observationDetails?.description,
        issueSubtypeId: this.newIssueForm.controls.issueSubtypeId.value,
        status: 'open',
        dueDate: dueDate,
      };

      this.bimService
        .api_postAccIssue(
          this.newIssueForm.controls.project_id.value.split('.')[1], // container id
          this.bimService.getAccessToken(),
          body,
        )
        .subscribe(
          async (res) => {
            if (res) {
              this.saveAutodeskAccount();
              this.updateObservation(res.body.id);
              await this._project.fetchProjectDetails(); // fetch project details
              this._toaster.success(
                this.transloco.translate('Observation has been successfully added to ACC'),
              );
              this.dialogRef.close(true);
            }
          },
          (error) => {
            this.isLoading = false;
            this._toaster.error(error.error.title);
          },
        );
    }
  }

  onNoClick() {
    this.dialogRef.close();
  }

  async getBimIssuesTypes(project_id: string): Promise<any> {
    this.isLoading = true;
    const permission = await this.getBimCreateIssuePermission(project_id);
    if (permission) {
      this.selectedProject = true;
      await this.bimService.getBimIssuesTypes(project_id).then((res) => {
        if (res) this.issue_types = res;
      });
    } else {
      this._toaster.warning(
        this.transloco.translate('You do not have permission to create issues to this project'),
      );
      this.selectedProject = false; // to disable the list of types
    }
  }
  getBimCreateIssuePermission(project_id: string): Promise<boolean> {
    this.isLoading = true;
    return new Promise((resolve, reject) => {
      this.bimService
        .api_getCreateIssuePermission(project_id.split('.')[1], this.bimService.getAccessToken())
        .subscribe(
          (response) => {
            this.isLoading = false;

            // Check if "issues" object exists and contains "new"
            const hasPermission = !!(response.issues && response.issues.new);

            resolve(hasPermission);
          },
          (error) => {
            this.isLoading = false;
            resolve(false); // Return false if an error occurs
          },
        );
    });
  }

  saveAutodeskAccount() {
    //storing autodesk data in the backend
    let body = {
      project_id: this._project.id,
      token: this.bimService.getAccessToken(),
      refresh_token: this.bimService.getAccessToken(), // as it required so pass anything till BE refactor
      token_expiry_date: 2700, // as it required so pass anything till BE refactor
      autodesk_project_id: this.newIssueForm.controls.project_id.value,
      autodesk_hub_id: this.bimService.getBim360HubId(),
      autodesk_container_id: this.bimService.getBim360ProjectId().split('.')[1],
      autodesk_root_folder_id: this.bimService.getBim360RootFolderId(),
      autodesk_project_name: this.bimProjectList.find(
        (p) => p.id === this.newIssueForm.controls.project_id.value,
      ).name,
    };
    this.bimService.saveAutodeskAccount(body).subscribe();
  }

  updateObservation(acc_issue_id: any): void {
    const body = {
      project_id: this._project.id,
      observation_id: this.data.observationDetails?.id,
      autodesk_issue_id: acc_issue_id,
    };
    this.observationsService.updateObservation(body).subscribe();
  }
}
